import { useState, useEffect, useRef, lazy, Suspense, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useBundles } from "@/hooks/use-bundles";
import { useBooks } from "@/hooks/use-books";
import { useEnhancedEarlyAccess } from "@/hooks/use-enhanced-early-access";
import { useOptimisticPayment } from "@/hooks/use-optimistic-payment";
import { useReadingProgress } from "@/hooks/use-reading-progress";
import { PendingPaymentChecker } from "@/components/PendingPaymentChecker";
import { Book } from "@/types/book";
import { formatDistanceToNow } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { HeroSection } from "@/components/HeroSection";
import { BundleShowcase } from "@/components/reader/BundleShowcase";
import { ReadingProgress } from "@/components/ReadingProgress";
import { ReadingJourneyStories } from "@/components/ReadingJourneyStories";
import { FreePreviewSection } from "@/components/FreePreviewSection";
import { FreeBooksSection } from "@/components/FreeBooksSection";
import { useAuth } from "@/components/AuthProvider";
import { ErrorDisplay } from "@/components/ErrorDisplay";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { useBatchIndividualAccess } from "@/hooks/use-batch-individual-access";
import { useBookAvailability } from "@/hooks/use-book-availability";
import { navigateToBook } from "@/utils/reading-navigation";
import { AuthModal } from "@/components/AuthModal";
import { ProfileSelectionModal } from "@/components/ProfileSelectionModal";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { useAppConfig } from "@/hooks/use-app-config";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useExpressCheckout } from "@/hooks/use-express-checkout";
import { useSetupIntentRedirect } from "@/hooks/use-setup-intent-redirect";
import { SubscriptionPurchaseButton } from "@/components/SubscriptionPurchaseButton";
import { Crown } from "lucide-react";
import { AnimatedBookBackground } from "@/components/animations/AnimatedBookBackground";
import { BookSection } from "@/components/BookSection";
import { FreeTrialBanner } from "@/components/FreeTrialBanner";
import { FreeTrialCard } from "@/components/FreeTrialCard";
import { RecentlyAddedSection } from "@/components/RecentlyAddedSection";
import { ComingSoonSection } from "@/components/ComingSoonSection";

import { PermanentBundlesSection } from "@/components/PermanentBundlesSection";
import { useEnhancedReadingList } from "@/hooks/use-enhanced-reading-list";
import { useIsMobile } from "@/hooks/use-mobile";
import { DeferredRender, LazyNetflixBrowse, LazyCinemaCarousel, LazyPaymentSuccessAnimation, CinemaCarouselPlaceholder, NetflixBrowsePlaceholder } from "@/components/LazyComponents";
import { captureUTMParams } from "@/utils/marketing";

// Lazy load BookDialog - only needed when a book is selected
const BookDialog = lazy(() => import("@/components/BookDialog").then(m => ({
  default: m.BookDialog
})));
export default function Index() {
  const {
    showSuccessAnimation
  } = useExpressCheckout(); // Handle express checkout success
  const {
    isProcessing: isSetupIntentProcessing,
    showSuccess: setupIntentSuccess
  } = useSetupIntentRedirect(); // Handle redirect payment methods
  const [showAnimation, setShowAnimation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [bookDialogReferrer, setBookDialogReferrer] = useState<string | null>(null);

  // Get app configuration first to determine if bundles should be enabled
  const {
    data: appConfig
  } = useAppConfig();
  const queryClientInstance = useQueryClient();
  const {
    data: subscriptionStatus,
    refetch: refetchSubscriptionStatus
  } = useSubscriptionStatus();

  // Only enable bundles in bundle mode
  const bundlesEnabled = appConfig?.current_model === 'bundle';
  const {
    activeBundle,
    permanentBundles,
    expiredBundles,
    hasActiveAccess,
    permanentBundleAccess,
    expiredBundleAccess,
    queryClient,
    refreshAll,
    isLoading: isBundlesLoading,
    isError: isBundlesError
  } = useBundles(bundlesEnabled);
  const {
    activeBooks,
    upcomingBooks,
    expiredBooks,
    previewBooks,
    allBooks,
    getBooksByBundleId,
    isLoading: isBooksLoading,
    isError: isBooksError
  } = useBooks();
  const {
    currentStreak,
    totalBooksRead,
    readingGoal,
    booksThisMonth,
    updateReadingGoal,
    isUpdatingGoal,
    isLoading: isReadingStatsLoading,
    isError: isReadingStatsError
  } = useReadingProgress();
  const {
    session
  } = useAuth();
  const {
    hasOptimisticAccess,
    isProcessing
  } = useOptimisticPayment();
  const {
    isAuthModalOpen,
    isProfileSelectionOpen,
    authContext,
    openAuthModal,
    closeAuthModal,
    openProfileSelection,
    closeProfileSelection,
    transitionToProfileSelection
  } = useAuthModal();

  // Reading list functionality
  const {
    isBookInReadingList,
    toggleBookInReadingList,
    isAdding: isReadingListLoading,
    isRemoving: isRemovingFromReadingList
  } = useEnhancedReadingList();
  
  const isMobile = useIsMobile();
  const allVisibleBookIds = [...activeBooks.map(b => Number(b.id)), ...upcomingBooks.map(b => Number(b.id)), ...expiredBooks.map(b => Number(b.id))];
  const {
    data: individualAccessMap
  } = useBatchIndividualAccess(allVisibleBookIds);

  // Use the same robust admin check as other components
  const {
    data: isAdmin
  } = useQuery({
    queryKey: ['isAdmin', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return false;
      const {
        data: accountData,
        error: accountError
      } = await supabase.from('user_accounts').select('is_admin').eq('user_id', session.user.id).maybeSingle();
      if (!accountError && accountData) {
        return accountData.is_admin;
      }
      const {
        data: rpcData,
        error: rpcError
      } = await supabase.rpc('is_admin_new', {
        user_id: session.user.id
      });
      if (rpcError) {
        console.error('Error checking admin status:', rpcError);
        return false;
      }
      return rpcData ?? false;
    },
    enabled: !!session?.user?.id
  });

  const hasDataLoadingError = isBundlesError || isBooksError;

  // Get the first available preview book
  const previewBook = previewBooks.length > 0 ? previewBooks[0] : null;

  // Enhanced access checking with optimistic updates and individual purchases
  const checkBundleAccess = (bundleId: number, bundleType: string) => {
    // Check optimistic access first
    if (hasOptimisticAccess(bundleId)) return true;

    // Admin users have access to everything
    if (isAdmin) return true;

    // Check actual access based on bundle type
    if (bundleType === 'temporal') {
      return bundleId === activeBundle?.id ? !!hasActiveAccess : !!expiredBundleAccess[bundleId];
    }
    return !!permanentBundleAccess[bundleId];
  };
  const checkBookAccess = (book: Book | null) => {
    if (!book) return false;

    // Check if it's a preview book first - everyone has access to preview books
    if (book.is_preview) return true;

    // CRITICAL: Check subscription access (active OR cancelled with time left)
    if (subscriptionStatus?.subscribed) {
      if (subscriptionStatus.subscription_status === 'active') {
        return true;
      }
      if (subscriptionStatus.subscription_status === 'cancelled' && subscriptionStatus.current_period_end) {
        const endDate = new Date(subscriptionStatus.current_period_end);
        if (endDate > new Date()) {
          return true; // Still has access until period ends
        }
      }
    }

    // Admin users have access to everything
    if (isAdmin) return true;

    // If book has no bundle_id, check individual access only
    if (!book.bundle_id) {
      if (session?.user?.id && individualAccessMap) {
        return individualAccessMap.get(Number(book.id)) || false;
      }
      return false;
    }

    // Get bundle type from the actual bundle objects, not requiring book.bundle
    let bundleType = 'temporal'; // default fallback

    // Try to find the bundle in our bundle lists to get the correct type
    if (book.bundle_id === activeBundle?.id) {
      bundleType = activeBundle.bundle_type;
    } else {
      const permanentBundle = permanentBundles.find(b => b.id === book.bundle_id);
      if (permanentBundle) {
        bundleType = permanentBundle.bundle_type;
      } else {
        const expiredBundle = expiredBundles.find(b => b.id === book.bundle_id);
        if (expiredBundle) {
          bundleType = expiredBundle.bundle_type;
        }
      }
    }
    const bundleAccess = checkBundleAccess(book.bundle_id, bundleType);

    // Check individual book access using the batched data (instant lookup)
    if (!bundleAccess && session?.user?.id && individualAccessMap) {
      const hasIndividualAccess = individualAccessMap.get(Number(book.id)) || false;
      if (hasIndividualAccess) return true;
    }

    return bundleAccess;
  };
  const handleActiveBundleAccess = async () => {
    console.log("Initiating active bundle purchase", {
      bundleId: activeBundle?.id
    });
  };
  const handlePreviewReadNow = (book: Book, currentPage?: number) => {
    navigateToBook(Number(book.id), navigate, {
      currentPage
    });
  };
  const hasAccessToSelectedBook = checkBookAccess(selectedBook);
  const showBundlePrice = selectedBook && activeBundle && selectedBook.bundle_id === activeBundle.id && !checkBundleAccess(activeBundle.id, 'temporal');

  // Capture UTM params on mount for marketing tracking
  useEffect(() => {
    captureUTMParams();
  }, []);

  // Handle ?book=ID param to open BookDialog from external navigation (e.g., Jukebox)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bookIdParam = params.get('book');
    if (bookIdParam && allBooks.length > 0) {
      const bookId = parseInt(bookIdParam, 10);
      const foundBook = allBooks.find(b => Number(b.id) === bookId);
      if (foundBook) {
        // Store referrer from navigation state if coming from another page
        const referrer = location.state?.from || null;
        setBookDialogReferrer(referrer);
        setSelectedBook(foundBook);
        // Clean up URL without causing redirect
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [location.search, allBooks, location.state]);

  // iOS-style overscroll stretch effect - reads native rubber-band scroll
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 0) {
        const stretch = Math.min(Math.abs(y), 120);
        document.documentElement.style.setProperty('--overscroll', `${stretch}px`);
      } else {
        document.documentElement.style.setProperty('--overscroll', '0px');
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  // Force refresh subscription status when landing from payment
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment_success') === 'true') {
      console.log('[Index] Payment success detected, forcing subscription status refresh');

      // Invalidate and refetch subscription status immediately
      queryClientInstance.invalidateQueries({
        queryKey: ['subscriptionStatus']
      });
      refetchSubscriptionStatus();

      // Clean up URL without causing redirect
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, queryClientInstance, refetchSubscriptionStatus]);

  // Show animation when express checkout flag is present
  useEffect(() => {
    if (showSuccessAnimation) {
      setShowAnimation(true);
    }
  }, [showSuccessAnimation]);

  return <div className="bg-background pb-16 md:pb-8 relative" style={{
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)',
    overscrollBehaviorY: 'auto'
  }}>
      
      <HeroSection onBookSelect={setSelectedBook} onOpenAuthModal={openAuthModal} onReadingListToggle={toggleBookInReadingList} isInReadingList={isBookInReadingList} isReadingListLoading={isReadingListLoading || isRemovingFromReadingList} />
      
      
      {/* Cinema-style carousel - lazy loaded with proper height placeholder */}
      <DeferredRender rootMargin="100px" placeholder={<CinemaCarouselPlaceholder />}>
        <LazyCinemaCarousel />
      </DeferredRender>
      
      {/* Reading Journey Stories - IG style horizontal scroll */}
      {appConfig?.current_model === 'subscription' && (
        <ReadingJourneyStories 
          currentStreak={currentStreak} 
          totalBooksRead={totalBooksRead} 
          readingGoal={readingGoal} 
          booksThisMonth={booksThisMonth} 
          isLoading={isReadingStatsLoading} 
          isError={isReadingStatsError} 
          onOpenAuthModal={openAuthModal}
          updateReadingGoal={updateReadingGoal}
          isUpdatingGoal={isUpdatingGoal}
        />
      )}
      
      
      
      {isProcessing && <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="glass-morphism-modal p-6 rounded-lg shadow-xl border flex flex-col items-center">
            <p className="text-white font-medium">Processing your payment...</p>
            <p className="text-white/70 text-sm mt-2">Please don't close this window</p>
          </div>
        </div>}
      
      {hasDataLoadingError && !isBundlesLoading && !isBooksLoading && <ErrorDisplay queryClient={queryClient} actionLabel="Refresh Content" action={refreshAll} />}
      
      {!hasDataLoadingError && <>

          {bundlesEnabled && activeBundle && <div className="mb-4 md:mb-6">
              <BundleShowcase bundle={activeBundle} books={activeBooks} hasAccess={checkBundleAccess(activeBundle.id, 'temporal')} isUnlocked={true} onPurchase={handleActiveBundleAccess} onBookClick={setSelectedBook} isLoading={isBooksLoading} />
            </div>}

          {bundlesEnabled && permanentBundles.filter(b => b.title.toLowerCase() !== 'funbox').length > 0 && <div className="mb-4 md:mb-6">
              {permanentBundles.filter(b => b.title.toLowerCase() !== 'funbox').map(bundle => {
          const bundleBooks = activeBooks.filter(book => book.bundle_id === bundle.id);
          return <BundleShowcase key={bundle.id} bundle={bundle} books={bundleBooks} hasAccess={checkBundleAccess(bundle.id, 'permanent')} isUnlocked={true} onPurchase={() => console.log("Purchase permanent bundle", bundle.id)} onBookClick={setSelectedBook} isLoading={isBooksLoading} />;
        })}
            </div>}
          

          {bundlesEnabled && (() => {
        const funBoxBundle = permanentBundles.find(b => b.title.toLowerCase() === 'funbox');
        if (!funBoxBundle) return null;
        const bundleBooks = activeBooks.filter(book => book.bundle_id === funBoxBundle.id);
        return <div className="mb-4 md:mb-6">
                <BundleShowcase bundle={funBoxBundle} books={bundleBooks} hasAccess={checkBundleAccess(funBoxBundle.id, 'permanent')} isUnlocked={true} onPurchase={() => console.log("Purchase FunBox bundle", funBoxBundle.id)} onBookClick={setSelectedBook} isLoading={isBooksLoading} />
              </div>;
      })()}

          {/* Free Books Section */}
          {previewBooks.length > 0 && subscriptionStatus?.subscription_status !== 'active' && (
            <div className="px-5 md:px-12 xl:px-16 mb-4 relative z-0">
              <FreeBooksSection
                books={previewBooks}
                onBookClick={setSelectedBook}
                isLoading={isBooksLoading}
              />
            </div>
          )}

          {/* Netflix-Style Tag-Based Browse - lazy loaded with proper height placeholder */}
          <DeferredRender rootMargin="200px" placeholder={<NetflixBrowsePlaceholder />} minHeight="400px">
            <div className="mt-4 md:mt-6">
              <LazyNetflixBrowse onBookClick={setSelectedBook} />
            </div>
          </DeferredRender>

          {/* Subscription CTA - Under FunBox (NetflixBrowse) */}
          {/* Hide if active OR if cancelled but still has time left */}
          {appConfig?.current_model === 'subscription' && (!session || (
            subscriptionStatus?.subscription_status !== 'active' && 
            !(subscriptionStatus?.subscription_status === 'cancelled' && 
              subscriptionStatus?.current_period_end && 
              new Date(subscriptionStatus.current_period_end) > new Date())
          )) && (
            <div className="px-5 md:px-12 xl:px-16 mb-8">
              <FreeTrialCard />
            </div>
          )}
        </>}
      
      <FreeTrialBanner />
      
      {selectedBook && <Suspense fallback={null}>
          <BookDialog book={selectedBook} isOpen={!!selectedBook} onClose={() => {
        // Navigate back to referrer if we came from another page (e.g., Jukebox)
        if (bookDialogReferrer) {
          navigate(bookDialogReferrer);
          setBookDialogReferrer(null);
        }
        setSelectedBook(null);
      }} onReadNow={() => {
        navigateToBook(Number(selectedBook.id), navigate);
      }} onPurchase={handleActiveBundleAccess} hasAccess={hasAccessToSelectedBook || isAdmin} bundlePrice={showBundlePrice ? activeBundle?.price : undefined} bundleId={selectedBook?.bundle_id} isEarlyAccess={selectedBook?.bundle?.status === 'upcoming'} onBookClick={setSelectedBook} allBooks={allBooks} />
        </Suspense>}

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} onTransitionToProfileSelection={transitionToProfileSelection} context={authContext} />

      {/* Profile Selection Modal */}
      <ProfileSelectionModal isOpen={isProfileSelectionOpen} onClose={closeProfileSelection} />
      
      {/* Payment Success Animation - Lazy loaded */}
      {showAnimation && <Suspense fallback={null}>
          <LazyPaymentSuccessAnimation onComplete={() => setShowAnimation(false)} />
        </Suspense>}
    </div>;
}